/**
 * Strip thumbnails follow the live edit (owner 2026-10-10: "the thumbnails on the bottom previews don't update
 * when I rotate the images"). Each card shows its file's EFFECTIVE edit - the batch as it lands on that photo, or
 * its own crop - which is exactly the state the export renders (model/batch exportStateForMember, the editor's
 * stateFor): rotate (quarter turns and the fine angle with its corner fill), flip, crop (rect or circle), resize.
 * It is drawn with the one render pipeline the stage and the export use (model/render), capped at THUMB_EDGE, and
 * the card also states the output size next to the original.
 *
 * Pure helpers only. The editor (editor.component renderThumbs) schedules the drawing off the hot path - debounced
 * against slider-rate edits, one card at a time between idle periods, selected photo first, a superseded pass
 * dropped - and keeps each card by `thumbKey` so an unchanged file is never redrawn. Memory stays as it was: a card
 * is one ≤128 px canvas; the untouched case IS the source thumb; a photo whose pixels were let go (bounded memory)
 * is drawn from that source thumb, never decoded again for a card.
 */
import { EditState, Size, isIdentity, savedSize } from './edit-stack';
import { render, Source, MipCache } from './render';
import { isUnsized } from './batch';

/** The longer edge of a strip card's picture, in canvas px (the source thumb's size since the strip exists). */
export const THUMB_EDGE = 128;

/** `size` fitted to THUMB_EDGE on its longer edge (a smaller photo is scaled up to it, as the source thumb always was). */
export function fitThumb(size: Size, edge = THUMB_EDGE): Size {
    const k = edge / Math.max(size.width, size.height, 1);
    return { width: Math.max(1, Math.round(size.width * k)), height: Math.max(1, Math.round(size.height * k)) };
}

/** What one card shows: the file and the exact edit on it. Same key = the picture already drawn is current. */
export function thumbKey(id: string, st: EditState): string {
    return `${id}|${st.source.width}x${st.source.height}|${JSON.stringify(st.ops)}`;
}

/** An op that changes pixels applies (a flip counts; a free full-frame crop or an untouched resize does not). */
export function isEdited(st: EditState): boolean { return st.ops.some(op => !isIdentity(op)); }

/** The card's output size: the file as downloaded (savedSize: an ICO says its square), from the file's view state -
 *  the export's state but for view-only markers (model/batch exportStateForMember), so the same pixels - or null when
 *  the file's size is not known here (RAW on the server route: no size to derive it from). */
export function thumbOut(st: EditState | null | undefined, size: Size | null | undefined, format: string): Size | null {
    if (!st || isUnsized(size)) return null;
    return savedSize(st, format);
}

/**
 * The card's picture: `st` drawn from `src` at no more than THUMB_EDGE on its longer edge. `src` is the photo (its
 * decoded pixels, through its `mips` so the 1/16 level is shared and built once) or - once the pixels were let go -
 * its ≤128 px source thumb, in which case `sourceSize` names the pixels `st.source` refers to and the mip cache is
 * not used (a level of the thumb is not a level of the photo). A tiny photo stays its own size (previews never draw
 * more pixels than the source has).
 */
export async function renderThumb(src: Source, st: EditState, o: { mips?: MipCache; sourceSize?: Size } = {}): Promise<HTMLCanvasElement> {
    return render(src, st, { scale: 1, maxEdge: THUMB_EDGE, mips: o.sourceSize ? undefined : o.mips, sourceSize: o.sourceSize });
}
